import useProfile from "@/hooks/use-profile";
import SystemSettingRepository from "@/utilities/repositories/SystemSetting";
import { Roles } from "@/utilities/types/Users";
import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

interface TopBarHeightContextValue {
  topBarHeight: number;
  topBarRef: React.RefObject<HTMLDivElement | null>;
  setTopBarHeight: React.Dispatch<React.SetStateAction<number>>;
  topBarMessage: string | null;
}

const TopBarHeightContext = createContext<TopBarHeightContextValue>({
  topBarHeight: 0,
  topBarRef: { current: null },
  setTopBarHeight: () => {},
  topBarMessage: null,
});

export function useTopBarHeight() {
  return useContext(TopBarHeightContext);
}

interface TopBarHeightProviderProps {
  children: ReactNode;
}

export function TopBarHeightProvider({ children }: TopBarHeightProviderProps) {
  const topBarRef = useRef<HTMLDivElement | null>(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const [topBarMessage, setTopBarMessage] = useState<string | null>(null);
  const { profile } = useProfile();

  const fetchTopBarMessage = useCallback(async () => {
    if (profile?.role === Roles.ADMIN) {
      setTopBarMessage(null);
      setTopBarHeight(0);
      return;
    }

    try {
      const response = await SystemSettingRepository.getAgentDashboardTopBar();
      if (response?.data) {
        const isActive = response.data.status === "active";
        const message = response.data.data as { message: string };
        const hasMessage =
          isActive && !!message?.message && message.message.trim() !== "";

        setTopBarMessage(hasMessage ? message.message : null);
        setTopBarHeight(
          hasMessage && topBarRef.current ? topBarRef.current.offsetHeight : 0,
        );
      } else {
        setTopBarMessage(null);
        setTopBarHeight(0);
      }
    } catch {
      setTopBarMessage(null);
      setTopBarHeight(0);
    }
  }, [profile?.role]);

  useEffect(() => {
    fetchTopBarMessage();

    if (profile) {
      const timer = setTimeout(fetchTopBarMessage, 100);
      return () => clearTimeout(timer);
    }

    window.addEventListener("resize", fetchTopBarMessage);
    return () => window.removeEventListener("resize", fetchTopBarMessage);
  }, [profile?.id, profile?.role, fetchTopBarMessage]);

  return (
    <TopBarHeightContext.Provider
      value={{ topBarHeight, topBarRef, setTopBarHeight, topBarMessage }}
    >
      {children}
    </TopBarHeightContext.Provider>
  );
}
